import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createMirrorAuthSessionScope } from '@/lib/mirrorAuthSession';
import type { ProactiveGuard } from '@/lib/proactiveAssistant';

interface IdentityState {
  ready: boolean;
  userId: string | null;
  error: boolean;
  generation: number;
  guard?: ProactiveGuard;
}

/** Local cancellation identity. The server still authenticates and authorizes requests. */
export function useProactiveIdentity() {
  const [state, setState] = useState<IdentityState>({
    ready: false, userId: null, error: false, generation: 0,
  });
  const [attempt, setAttempt] = useState(0);
  const epoch = useRef(0);
  useEffect(() => {
    let alive = true;
    // Capture the ref object, so cleanup increments the current generation, not an old value.
    const activeEpoch = epoch;
    const scope = createMirrorAuthSessionScope();
    let currentGuard: ProactiveGuard | undefined;
    const apply = (session: unknown) => {
      if (!alive) return;
      const observed = scope.observeSession(session);
      if (observed.changed) {
        activeEpoch.current++;
        const lease = scope.capture();
        currentGuard = lease ? {
          authSessionId: lease.authSessionId,
          isCurrent: () => alive && scope.isCurrent(lease),
        } : undefined;
      }
      setState({
        ready: true, userId: observed.identity?.ownerId ?? null,
        error: false, generation: activeEpoch.current, guard: currentGuard,
      });
    };
    const revision = scope.revision();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => apply(session));
    const timer = window.setTimeout(() => {
      if (alive && scope.revision() === revision) {
        setState({ ready: true, userId: null, error: true, generation: activeEpoch.current });
      }
    }, 12000);
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!alive || scope.revision() !== revision) return;
      if (error) throw error;
      apply(data.session);
    }).catch(() => {
      if (alive && scope.revision() === revision) {
        setState({ ready: true, userId: null, error: true, generation: activeEpoch.current });
      }
    }).finally(() => window.clearTimeout(timer));
    return () => {
      alive = false;
      activeEpoch.current++;
      scope.invalidate();
      subscription.unsubscribe();
      window.clearTimeout(timer);
    };
  }, [attempt]);
  const retry = useCallback(() => {
    setState({ ready: false, userId: null, error: false, generation: epoch.current });
    setAttempt(value => value + 1);
  }, []);
  return { ...state, retry };
}
